/**
 * WebRTCContext — manages all media connections for in-game video/audio and
 * host screen sharing, powered by LiveKit Cloud.
 *
 * Architecture: SFU via LiveKit Cloud. Each participant connects to a LiveKit
 * room identified by the game ID. The Cloud Function `getLiveKitToken` mints
 * a signed JWT after verifying the user is a game participant.
 *
 * The context is rendered by GamePage and is only active during a live game.
 * In-person mode disables all media entirely (returns null context).
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  useMemo,
} from 'react';
import { createPortal } from 'react-dom';
import {
  LiveKitRoom,
  useLocalParticipant,
  useTracks,
  useRoomContext,
  useConnectionState,
} from '@livekit/components-react';
import { useTranslation } from 'react-i18next';
import MediaSetupScreen from '../components/MediaSetupScreen';
import { Track, AudioPresets, ConnectionState, DefaultReconnectPolicy, DisconnectReason } from 'livekit-client';
import { KrispNoiseFilter, isKrispNoiseFilterSupported } from '@livekit/krisp-noise-filter';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useAuth } from './AuthContext';
import { logError } from '../utils/logger';
import { kickPlayer } from '../services/firebase/games';
import { CHATTERS_ID } from '../constants/game';
import { setVisualizerGameContext } from '../components/AudioVisualizer';
import app from '../firebase';

// localStorage keys for first-time permission warnings (unchanged from P2P impl)
const WARNED_CAMERA_KEY = 'tmg_warned_camera';
const WARNED_MIC_KEY = 'tmg_warned_mic';

// localStorage keys for persisted audio settings
const AUDIO_CONSTRAINTS_KEY = 'tmg_audio_constraints';
const KRISP_ENABLED_KEY = 'tmg_krisp_enabled';

// Scoped by uid+gameId so refreshing in one game doesn't bleed into another.
const wantMediaKey = (type, uid, gId) => `tmg_want_${type}_${uid}_${gId}`;

// Video resolution presets — used when manually enabling camera or changing resolution.
const VIDEO_PRESETS = {
  '240p': { width: 426, height: 240 },
  '360p': { width: 640, height: 360 },
  '480p': { width: 854, height: 480 },
  '720p': { width: 1280, height: 720 },
  '1080p': { width: 1920, height: 1080 },
};

const DEFAULT_AUDIO_CONSTRAINTS = {
  echoCancellation: true,
  noiseSuppression: true,
  autoGainControl: true,
};

// ── No-op stubs ───────────────────────────────────────────────────────────────
// P2P-era recovery methods. LiveKit auto-reconnects, so these are stable no-ops
// at module scope — consumers can destructure them without triggering re-renders.
const retryStreamBinding = () => {};
const reconnectPeer = () => {};

// ── Context ───────────────────────────────────────────────────────────────────

const WebRTCContext = createContext(null);

// eslint-disable-next-line react-refresh/only-export-components
export function useWebRTC() {
  return useContext(WebRTCContext);
}

// ── Provider ──────────────────────────────────────────────────────────────────

/**
 * Top-level provider. Disables all media when inPersonMode is true.
 * Otherwise, fetches a LiveKit token and renders the room connection.
 */
export function WebRTCProvider({
  gameId,
  playerIds,
  hostId,
  isChatter,
  inPersonMode = false,
  gameActive = false,
  children,
}) {
  if (inPersonMode) {
    return (
      <WebRTCContext.Provider value={null}>
        {children}
      </WebRTCContext.Provider>
    );
  }

  return (
    <LiveKitRoomConnector
      gameId={gameId}
      playerIds={playerIds}
      hostId={hostId}
      isChatter={isChatter}
      gameActive={gameActive}
    >
      {children}
    </LiveKitRoomConnector>
  );
}

// ── LiveKit room connector (fetches token, renders <LiveKitRoom>) ─────────────

// mediaChoice state machine:
//   'loading'    — token not yet received
//   'this'       — token received; connect with full WebRTC
//   'displaced'  — was the active device but LiveKit kicked us (DUPLICATE_IDENTITY)
//
// If the same user opens the game on a second device, LiveKit boots the first
// device (DUPLICATE_IDENTITY), which triggers the 'displaced' state and shows
// the Displaced banner with a Reclaim button. We no longer detect existing
// connections server-side because that caused false "already connected" modals
// on page refresh (ghost participants linger for a few seconds after disconnect).
const LIVEKIT_ROOM_OPTIONS = {
  adaptiveStream: false,
  dynacast: true,
  reconnectPolicy: new DefaultReconnectPolicy(),
  publishDefaults: {
    audioPreset: AudioPresets.musicHighQualityStereo,
  },
  screenShareCaptureDefaults: {
    audio: {
      echoCancellation: false,
      noiseSuppression: false,
      autoGainControl: false,
      sampleRate: 48000,
      channelCount: 2,
    },
  },
};

function LiveKitRoomConnector({ gameId, playerIds, hostId, isChatter, gameActive, children }) {
  const { user, profile } = useAuth();
  const [token, setToken] = useState(null);
  const [mediaChoice, setMediaChoice] = useState('loading');

  useEffect(() => {
    if (!user || !gameId || isChatter) return;

    const functions = getFunctions(app, 'europe-west2');
    const getLiveKitToken = httpsCallable(functions, 'getLiveKitToken');

    getLiveKitToken({
      roomName: gameId,
      participantName: profile?.username || user.uid,
    })
      .then((result) => {
        setToken(result.data.token);
        setMediaChoice('this');
      })
      .catch(logError);
  }, [user, gameId, isChatter, profile?.username]);

  const handleDisplaced = () => setMediaChoice('displaced');

  // Render the LiveKit room only when this device is the chosen primary.
  if (mediaChoice === 'this' && token) {
    return (
      <LiveKitRoom
        serverUrl={import.meta.env.VITE_LIVEKIT_URL}
        token={token}
        connect
        audio={false}
        video={false}
        options={LIVEKIT_ROOM_OPTIONS}
        style={{ display: 'contents' }}
      >
        <WebRTCProviderInner
          gameId={gameId}
          playerIds={playerIds}
          hostId={hostId}
          gameActive={gameActive}
          onDisplaced={handleDisplaced}
        >
          {children}
        </WebRTCProviderInner>
      </LiveKitRoom>
    );
  }

  // All non-'this' states (loading, displaced) render a null context so the
  // game UI continues to work without media.
  return (
    <WebRTCContext.Provider value={null}>
      {mediaChoice === 'displaced' && (
        <DisplacedBanner onReclaim={() => setMediaChoice('this')} />
      )}
      {children}
    </WebRTCContext.Provider>
  );
}

// ── Displaced banner (shown when this device was booted by another) ───────────

function DisplacedBanner({ onReclaim }) {
  const { t } = useTranslation();
  return createPortal(
    <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 w-full max-w-sm px-4">
      <div className="t-bg-surface border t-border rounded-xl shadow-lg p-3 flex flex-col gap-2">
        <p className="text-sm font-semibold t-text">{t('webrtc.displacedTitle')}</p>
        <p className="text-xs t-text-secondary">{t('webrtc.displacedDesc')}</p>
        <button
          onClick={onReclaim}
          className="t-btn-primary self-start px-3 py-1.5 rounded-lg text-xs font-semibold"
        >
          {t('webrtc.displacedReclaim')}
        </button>
      </div>
    </div>,
    document.body,
  );
}

// ── Inner provider (uses LiveKit hooks, must be inside <LiveKitRoom>) ─────────

function WebRTCProviderInner({ gameId, hostId, gameActive, children, onDisplaced }) {
  const { user } = useAuth();
  const myUid = user?.uid;
  const isHostUser = myUid === hostId;

  // ── LiveKit hooks ───────────────────────────────────────────────────────────
  const {
    localParticipant,
    isCameraEnabled: livekitCameraEnabled,
    isMicrophoneEnabled: livekitMicEnabled,
  } = useLocalParticipant();

  const connectionState = useConnectionState();
  // Tracks the previous connection state so we can distinguish first-connect from reconnect.
  const prevConnStateRef = useRef(null);
  // Guards the one-time localStorage restore so it only runs on the initial connect.
  const didInitialRestoreRef = useRef(false);

  const room = useRoomContext();

  // useTracks subscribes to all track publications so any change (mute/unmute,
  // publish/unpublish) triggers a re-render, which in turn rebuilds the maps below.
  const allTracks = useTracks(
    [
      Track.Source.Camera,
      Track.Source.Microphone,
      Track.Source.ScreenShare,
      Track.Source.ScreenShareAudio,
    ],
    { onlySubscribed: false },
  );

  // ── Local state ─────────────────────────────────────────────────────────────
  const [videoResolution, setVideoResolution] = useState('360p');
  const [localMutes, setLocalMutes] = useState({});
  const [localVolumes, setLocalVolumes] = useState({});
  const [localHidden, setLocalHidden] = useState({});
  const [pendingPermission, _setPendingPermission] = useState(null);
  const pendingPermissionRef = useRef(null);
  // Stable setter that keeps the ref and state in sync so confirmPermission /
  // cancelPermission can be useCallback([]) without reading stale state.
  const setPendingPermission = useCallback((val) => {
    pendingPermissionRef.current = val;
    _setPendingPermission(val);
  }, []);
  const pendingCameraResolverRef = useRef(null);
  const pendingMicResolverRef = useRef(null);

  // ── Audio settings state (persisted to localStorage) ───────────────────────
  const [selectedMicId, setSelectedMicId] = useState(null);
  const [selectedCameraId, setSelectedCameraId] = useState(null);
  const [audioConstraints, setAudioConstraints] = useState(() => {
    try {
      const saved = localStorage.getItem(AUDIO_CONSTRAINTS_KEY);
      if (saved) return { ...DEFAULT_AUDIO_CONSTRAINTS, ...JSON.parse(saved) };
    } catch (e) { logError(e); }
    return DEFAULT_AUDIO_CONSTRAINTS;
  });
  const [krispEnabled, setKrispEnabled] = useState(() => {
    return localStorage.getItem(KRISP_ENABLED_KEY) !== 'false';
  });

  // Whether Krisp is available in this browser — stable boolean, computed once on mount.
  const krispSupported = useMemo(() => isKrispNoiseFilterSupported(), []);
  // Stores the currently active processor so we can call setEnabled() for smooth toggling.
  const krispFilterRef = useRef(null);

  // Destroy the Krisp processor when the provider unmounts (game left/ended).
  useEffect(() => {
    return () => {
      krispFilterRef.current?.destroy().catch(() => {});
      krispFilterRef.current = null;
    };
  }, []);

  const canShareScreen = !!navigator.mediaDevices?.getDisplayMedia;

  // ── Derive remote stream maps from LiveKit track publications ───────────────
  // remoteStreamsByUid: Map<uid, Map<streamId, MediaStream>>
  //   streamId 'camera' → camera video + mic audio combined stream
  //   streamId 'screen' → host screen share video + audio stream
  //
  // playerMediaStates: uid → { cameraEnabled, micEnabled, isScreenSharing,
  //                             cameraStreamId, screenStreamId }
  //
  // We iterate allTracks (which is the actual dep that changes on mute/publish
  // events) and group by participant identity to build per-player stream maps.
  // Using a fixed streamId ('camera' / 'screen') removes the need for Firestore
  // coordination that the old P2P implementation required.
  const { remoteStreamsByUid, playerMediaStates } = useMemo(() => {
    const streamMap = new Map();
    const mediaStates = {};

    // Group track references by remote participant identity.
    const byUid = new Map();
    for (const trackRef of allTracks) {
      if (trackRef.participant.isLocal) continue;
      const uid = trackRef.participant.identity;
      if (!byUid.has(uid)) byUid.set(uid, {});
      byUid.get(uid)[trackRef.publication.source] = trackRef.publication;
    }

    for (const [uid, pubs] of byUid) {
      const participantStreams = new Map();

      const camPub = pubs[Track.Source.Camera];
      const micPub = pubs[Track.Source.Microphone];
      const screenPub = pubs[Track.Source.ScreenShare];
      const screenAudioPub = pubs[Track.Source.ScreenShareAudio];

      const camTrack = camPub?.track;
      const micTrack = micPub?.track;
      const screenTrack = screenPub?.track;
      const screenAudioTrack = screenAudioPub?.track;

      // Camera stream: combine video + audio so the <video> element plays both.
      // Create a mic-only stream even when camera is off so audio still reaches
      // the video element (volume/mute controls apply at the element level).
      if (camTrack?.mediaStreamTrack || micTrack?.mediaStreamTrack) {
        const rawTracks = [
          camTrack?.mediaStreamTrack,
          micTrack?.mediaStreamTrack,
        ].filter(Boolean);
        participantStreams.set('camera', new MediaStream(rawTracks));
      }

      // Screen share stream: video + optional system audio.
      if (screenTrack?.mediaStreamTrack) {
        const rawTracks = [
          screenTrack.mediaStreamTrack,
          screenAudioTrack?.mediaStreamTrack,
        ].filter(Boolean);
        participantStreams.set('screen', new MediaStream(rawTracks));
      }

      streamMap.set(uid, participantStreams);
      mediaStates[uid] = {
        cameraEnabled: !!(camTrack && !camPub?.isMuted),
        micEnabled: !!(micTrack && !micPub?.isMuted),
        isScreenSharing: !!screenTrack,
        cameraStreamId: 'camera',
        screenStreamId: 'screen',
      };
    }

    return { remoteStreamsByUid: streamMap, playerMediaStates: mediaStates };
  }, [allTracks]);

  // ── Derived local media values ──────────────────────────────────────────────

  // Camera state derived from LiveKit (avoids out-of-sync local state).
  const cameraEnabled = livekitCameraEnabled;
  const micEnabled = livekitMicEnabled;

  // Screen sharing state: boolean derived from local participant publications.
  // Wrapping in useMemo makes it a stable primitive dep for the value useMemo.
  const isScreenSharing = useMemo(
    () => !!(localParticipant?.getTrackPublication(Track.Source.ScreenShare)?.track),
    [allTracks, localParticipant], // eslint-disable-line react-hooks/exhaustive-deps
  );

  // Local camera stream for self-preview in PlayerVideoCard.
  const localCameraStream = useMemo(() => {
    const camTrack = localParticipant
      ?.getTrackPublication(Track.Source.Camera)
      ?.track?.mediaStreamTrack;
    if (!camTrack) return null;
    return new MediaStream([camTrack]);
  }, [livekitCameraEnabled, localParticipant]); // eslint-disable-line react-hooks/exhaustive-deps

  // Host screen streams — used by HostScreenView and the audio visualizer.
  // Memoized on the underlying MediaStreamTrack objects so new MediaStream wrappers
  // are only created when the actual tracks change (not on every useTracks fire).
  const localScreenPub = localParticipant?.getTrackPublication(Track.Source.ScreenShare);
  const localScreenAudioPub = localParticipant?.getTrackPublication(Track.Source.ScreenShareAudio);
  const localScreenVideoTrack = localScreenPub?.track?.mediaStreamTrack ?? null;
  const localScreenAudioTrack = localScreenAudioPub?.track?.mediaStreamTrack ?? null;

  const { hostScreenStream, hostScreenAudioStream } = useMemo(() => {
    if (isHostUser && localScreenVideoTrack) {
      const rawTracks = [localScreenVideoTrack, localScreenAudioTrack].filter(Boolean);
      const full = new MediaStream(rawTracks);
      const audio = localScreenAudioTrack ? new MediaStream([localScreenAudioTrack]) : null;
      return { hostScreenStream: full, hostScreenAudioStream: audio };
    }
    if (!isHostUser) {
      const hostStreamMap = remoteStreamsByUid.get(hostId);
      const screenStream = hostStreamMap?.get('screen') ?? null;
      if (screenStream) {
        const audioTracks = screenStream.getAudioTracks();
        return {
          hostScreenStream: screenStream,
          hostScreenAudioStream: audioTracks.length ? new MediaStream(audioTracks) : null,
        };
      }
    }
    return { hostScreenStream: null, hostScreenAudioStream: null };
  }, [isHostUser, localScreenVideoTrack, localScreenAudioTrack, remoteStreamsByUid, hostId]);

  const isHostScreenSharing = isHostUser
    ? isScreenSharing
    : (playerMediaStates[hostId]?.isScreenSharing ?? false);

  // ── Detect displacement by another device (same identity reconnected) ────────
  useEffect(() => {
    if (!room || !onDisplaced) return;
    const handleDisconnect = (reason) => {
      if (reason === DisconnectReason.DUPLICATE_IDENTITY) onDisplaced();
    };
    room.on('disconnected', handleDisconnect);
    return () => room.off('disconnected', handleDisconnect);
  }, [room, onDisplaced]);

  // ── Sync visualizer game context ────────────────────────────────────────────
  useEffect(() => {
    setVisualizerGameContext({
      isHost: isHostUser,
      inPersonMode: false,
      isHostScreenSharing,
      hostAudioStream: hostScreenAudioStream,
    });
  }); // intentionally no deps — runs every render to stay in sync

  useEffect(() => {
    return () => {
      setVisualizerGameContext({
        isHost: false,
        inPersonMode: true,
        isHostScreenSharing: false,
        hostAudioStream: null,
      });
    };
  }, []);

  // ── Permission warning helpers ──────────────────────────────────────────────

  // Uses refs for resolver + pendingPermissionRef so the callbacks are stable
  // (useCallback([], [])) and don't invalidate the context value useMemo.
  const requestPermission = useCallback(async (type) => {
    const warnedKey = type === 'camera' ? WARNED_CAMERA_KEY : WARNED_MIC_KEY;
    if (!localStorage.getItem(warnedKey)) {
      const resolverRef = type === 'camera' ? pendingCameraResolverRef : pendingMicResolverRef;
      const confirmed = await new Promise((resolve) => {
        resolverRef.current = resolve;
        setPendingPermission(type);
      });
      if (!confirmed) return false;
      localStorage.setItem(warnedKey, '1');
    }
    return true;
  }, [setPendingPermission]);

  const confirmPermission = useCallback(() => {
    const type = pendingPermissionRef.current;
    const resolverRef = type === 'camera' ? pendingCameraResolverRef : pendingMicResolverRef;
    setPendingPermission(null);
    resolverRef.current?.(true);
    resolverRef.current = null;
  }, [setPendingPermission]);

  const cancelPermission = useCallback(() => {
    const type = pendingPermissionRef.current;
    const resolverRef = type === 'camera' ? pendingCameraResolverRef : pendingMicResolverRef;
    setPendingPermission(null);
    resolverRef.current?.(false);
    resolverRef.current = null;
  }, [setPendingPermission]);

  // ── Krisp noise filter helpers ──────────────────────────────────────────────

  // Applies or removes the Krisp processor on the currently active mic track.
  // The filter instance is created once and reused; only setEnabled() toggles it.
  // setProcessor() is called on every enable to handle mic tracks being recreated
  // (e.g. after mic disable/re-enable or device switch).
  const applyKrispToMic = useCallback(async (enable) => {
    if (!krispSupported || !localParticipant) return;
    const pub = localParticipant.getTrackPublication(Track.Source.Microphone);
    const audioTrack = pub?.track;
    if (!audioTrack) return;
    try {
      if (enable) {
        if (!krispFilterRef.current) {
          krispFilterRef.current = KrispNoiseFilter();
        }
        await audioTrack.setProcessor(krispFilterRef.current);
        await krispFilterRef.current.setEnabled(true);
      } else if (krispFilterRef.current) {
        await krispFilterRef.current.setEnabled(false);
      }
    } catch (err) {
      logError(err);
    }
  }, [krispSupported, localParticipant]);

  // Restore camera/mic after a page refresh, and re-apply Krisp after a transient reconnect.
  useEffect(() => {
    const prev = prevConnStateRef.current;
    prevConnStateRef.current = connectionState;

    if (connectionState !== ConnectionState.Connected || !localParticipant) return;

    if (!didInitialRestoreRef.current) {
      // First connection in this page load — restore desired media state from localStorage.
      didInitialRestoreRef.current = true;
      const wantsCamera = localStorage.getItem(wantMediaKey('camera', myUid, gameId)) === '1';
      const wantsMic = localStorage.getItem(wantMediaKey('mic', myUid, gameId)) === '1';

      if (wantsCamera) {
        const preset = VIDEO_PRESETS[videoResolution] || VIDEO_PRESETS['360p'];
        localParticipant.setCameraEnabled(true, {
          deviceId: selectedCameraId ?? undefined,
          resolution: { width: preset.width, height: preset.height },
        }).catch(logError);
      }
      if (wantsMic) {
        localParticipant.setMicrophoneEnabled(true, {
          deviceId: selectedMicId ?? undefined,
          echoCancellation: audioConstraints.echoCancellation,
          noiseSuppression: audioConstraints.noiseSuppression,
          autoGainControl: audioConstraints.autoGainControl,
        }).then(async () => {
          if (krispEnabled) await applyKrispToMic(true);
        }).catch(logError);
      }
      return;
    }

    // Transient reconnect (network blip): re-apply Krisp since the mic track was recreated.
    if (prev === ConnectionState.Reconnecting || prev === ConnectionState.SignalReconnecting) {
      const micPub = localParticipant.getTrackPublication(Track.Source.Microphone);
      if (micPub?.track && krispEnabled) applyKrispToMic(true).catch(logError);
    }
  }, [connectionState, localParticipant]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Camera / mic toggles ────────────────────────────────────────────────────

  const toggleCamera = useCallback(async () => {
    if (!localParticipant) return;
    try {
      if (!cameraEnabled) {
        const ok = await requestPermission('camera');
        if (!ok) return;
        const preset = VIDEO_PRESETS[videoResolution] || VIDEO_PRESETS['360p'];
        await localParticipant.setCameraEnabled(true, {
          deviceId: selectedCameraId ?? undefined,
          resolution: { width: preset.width, height: preset.height },
        });
        localStorage.setItem(wantMediaKey('camera', myUid, gameId), '1');
      } else {
        await localParticipant.setCameraEnabled(false);
        localStorage.removeItem(wantMediaKey('camera', myUid, gameId));
      }
    } catch (err) {
      logError(err);
    }
  }, [cameraEnabled, videoResolution, selectedCameraId, myUid, gameId, localParticipant, requestPermission]);

  const toggleMic = useCallback(async () => {
    if (!localParticipant) return;
    try {
      if (!micEnabled) {
        const ok = await requestPermission('mic');
        if (!ok) return;
        await localParticipant.setMicrophoneEnabled(true, {
          deviceId: selectedMicId ?? undefined,
          echoCancellation: audioConstraints.echoCancellation,
          noiseSuppression: audioConstraints.noiseSuppression,
          autoGainControl: audioConstraints.autoGainControl,
        });
        if (krispEnabled) await applyKrispToMic(true);
        localStorage.setItem(wantMediaKey('mic', myUid, gameId), '1');
      } else {
        await localParticipant.setMicrophoneEnabled(false);
        localStorage.removeItem(wantMediaKey('mic', myUid, gameId));
      }
    } catch (err) {
      logError(err);
    }
  }, [micEnabled, selectedMicId, audioConstraints, krispEnabled, myUid, gameId, localParticipant, applyKrispToMic, requestPermission]);

  // ── Screen share (host only) ────────────────────────────────────────────────

  const startScreenShare = useCallback(async () => {
    if (!isHostUser || !canShareScreen || !localParticipant) return;
    try {
      await localParticipant.setScreenShareEnabled(true, {
        audio: {
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: false,
          sampleRate: 48000,
          channelCount: 2,
        },
      });
      setTimeout(() => window.dispatchEvent(new Event('resize')), 400);
    } catch (err) {
      logError(err);
    }
  }, [isHostUser, canShareScreen, localParticipant]);

  const stopScreenShare = useCallback(async () => {
    if (!localParticipant) return;
    try {
      await localParticipant.setScreenShareEnabled(false);
    } catch (err) {
      logError(err);
    }
  }, [localParticipant]);

  // ── Video resolution ────────────────────────────────────────────────────────

  const changeResolution = useCallback(async (resolution) => {
    setVideoResolution(resolution);
    if (!cameraEnabled || !localParticipant) return;
    const preset = VIDEO_PRESETS[resolution] || VIDEO_PRESETS['360p'];
    try {
      // Re-publish camera with new resolution constraints.
      await localParticipant.setCameraEnabled(false);
      await localParticipant.setCameraEnabled(true, {
        deviceId: selectedCameraId ?? undefined,
        resolution: { width: preset.width, height: preset.height },
      });
    } catch (err) {
      logError(err);
    }
  }, [cameraEnabled, selectedCameraId, localParticipant]);

  // ── Audio constraints (echoCancellation, noiseSuppression, autoGainControl) ─

  const changeAudioConstraints = useCallback(async (patch) => {
    const next = { ...audioConstraints, ...patch };
    setAudioConstraints(next);
    localStorage.setItem(AUDIO_CONSTRAINTS_KEY, JSON.stringify(next));
    if (!micEnabled || !localParticipant) return;
    try {
      await localParticipant.setMicrophoneEnabled(false);
      await localParticipant.setMicrophoneEnabled(true, {
        deviceId: selectedMicId ?? undefined,
        echoCancellation: next.echoCancellation,
        noiseSuppression: next.noiseSuppression,
        autoGainControl: next.autoGainControl,
      });
      if (krispEnabled) await applyKrispToMic(true);
    } catch (err) {
      logError(err);
    }
  }, [audioConstraints, micEnabled, selectedMicId, krispEnabled, localParticipant, applyKrispToMic]);

  // ── Krisp toggle ────────────────────────────────────────────────────────────

  const toggleKrisp = useCallback(async () => {
    const next = !krispEnabled;
    setKrispEnabled(next);
    localStorage.setItem(KRISP_ENABLED_KEY, String(next));
    if (micEnabled) await applyKrispToMic(next);
  }, [krispEnabled, micEnabled, applyKrispToMic]);

  // ── Device selection ────────────────────────────────────────────────────────

  const changeMicDevice = useCallback(async (deviceId) => {
    setSelectedMicId(deviceId);
    if (!micEnabled) return;
    try {
      // room.switchActiveDevice uses an exact deviceId constraint and properly
      // republishes the track — unlike setMicrophoneEnabled which uses an ideal
      // constraint that Chrome/Edge often ignores.
      await room.switchActiveDevice('audioinput', deviceId);
      if (krispEnabled) await applyKrispToMic(true);
    } catch (err) {
      logError(err);
    }
  }, [micEnabled, room, krispEnabled, applyKrispToMic]);

  const changeCameraDevice = useCallback(async (deviceId) => {
    setSelectedCameraId(deviceId);
    if (!cameraEnabled) return;
    try {
      await room.switchActiveDevice('videoinput', deviceId);
    } catch (err) {
      logError(err);
    }
  }, [cameraEnabled, room]);

  // ── Remote controls (local only — never sent to server) ────────────────────
  // Functional setters make these stable with empty deps arrays.

  const muteRemote = useCallback((uid) => setLocalMutes((prev) => ({ ...prev, [uid]: true })), []);
  const unmuteRemote = useCallback((uid) => setLocalMutes((prev) => ({ ...prev, [uid]: false })), []);
  const setRemoteVolume = useCallback((uid, vol) => setLocalVolumes((prev) => ({ ...prev, [uid]: vol })), []);
  const hideRemoteCamera = useCallback((uid) => setLocalHidden((prev) => ({ ...prev, [uid]: true })), []);
  const showRemoteCamera = useCallback((uid) => setLocalHidden((prev) => ({ ...prev, [uid]: false })), []);

  // ── Kick player ─────────────────────────────────────────────────────────────

  const kickPlayerFromGame = useCallback((uid) => kickPlayer(gameId, uid), [gameId]);

  // ── Media setup screen ─────────────────────────────────────────────────────
  // Shown once per page load so the user can configure camera/mic before entering.
  const [mediaSetupDone, setMediaSetupDone] = useState(false);

  // ── Disable all media (used by "Join without audio" on setup screen) ────────

  const disableAllMedia = useCallback(async () => {
    localStorage.removeItem(wantMediaKey('camera', myUid, gameId));
    localStorage.removeItem(wantMediaKey('mic', myUid, gameId));
    try {
      if (cameraEnabled) await localParticipant.setCameraEnabled(false);
      if (micEnabled) await localParticipant.setMicrophoneEnabled(false);
    } catch (err) { logError(err); }
  }, [cameraEnabled, micEnabled, myUid, gameId, localParticipant]);

  // ── Context value ───────────────────────────────────────────────────────────
  // Memoized so context consumers only re-render when values they actually use
  // change. All function references are stable (useCallback) so they don't
  // invalidate the memo on unrelated renders (e.g. useTracks firing for a
  // different player's mute event).

  const value = useMemo(() => ({
    // Local streams
    localCameraStream,
    localScreenStream: isHostUser ? (localScreenPub?.track?.mediaStream ?? null) : null,

    // Remote streams
    remoteStreamsByUid,

    // Media state (derived from LiveKit publications — replaces Firestore playerMedia)
    playerMediaStates,

    // Host screen streams
    hostScreenStream,
    hostScreenAudioStream,
    isHostScreenSharing,

    // Local state
    cameraEnabled,
    micEnabled,
    isScreenSharing,
    videoResolution,

    // Permission warning
    pendingPermission,
    confirmPermission,
    cancelPermission,

    // Controls
    toggleCamera,
    toggleMic,
    startScreenShare,
    stopScreenShare,
    changeResolution,
    canShareScreen,

    // Audio settings
    audioConstraints,
    changeAudioConstraints,
    krispEnabled,
    krispSupported,
    toggleKrisp,
    selectedMicId,
    selectedCameraId,
    changeMicDevice,
    changeCameraDevice,

    // Remote controls (local only)
    localMutes,
    localVolumes,
    localHidden,
    muteRemote,
    unmuteRemote,
    setRemoteVolume,
    hideRemoteCamera,
    showRemoteCamera,

    // Host only
    kickPlayer: kickPlayerFromGame,

    // Recovery stubs (no-op — LiveKit auto-reconnects; defined at module scope)
    retryStreamBinding,
    reconnectPeer,

    // Media setup
    disableAllMedia,
  }), [
    localCameraStream, isHostUser, localScreenPub, remoteStreamsByUid, playerMediaStates,
    hostScreenStream, hostScreenAudioStream, isHostScreenSharing,
    cameraEnabled, micEnabled, isScreenSharing, videoResolution,
    pendingPermission, confirmPermission, cancelPermission,
    toggleCamera, toggleMic, startScreenShare, stopScreenShare, changeResolution,
    canShareScreen, audioConstraints, changeAudioConstraints,
    krispEnabled, krispSupported, toggleKrisp,
    selectedMicId, selectedCameraId, changeMicDevice, changeCameraDevice,
    localMutes, localVolumes, localHidden,
    muteRemote, unmuteRemote, setRemoteVolume, hideRemoteCamera, showRemoteCamera,
    kickPlayerFromGame, disableAllMedia,
  ]);

  return (
    <WebRTCContext.Provider value={value}>
      {children}
      {!mediaSetupDone && (
        <MediaSetupScreen
          gameActive={gameActive}
          onDone={() => setMediaSetupDone(true)}
        />
      )}
    </WebRTCContext.Provider>
  );
}
